/**
 * ヘッダー右上のユーザーアイコン + アカウント切替ドロップダウン。
 *
 * - 現在のユーザー名・ロール表示
 * - 同一 email が他テナントにも所属している場合、ドロップダウンで一覧を表示
 * - クリックでアカウント切替（サーバーが新 JWT を Cookie セット）→ 全ページリロード
 * - ログアウトもここから
 */
import { useEffect, useRef, useState } from "react";
import { api } from "../api.js";

interface Membership {
  userId: string;
  tenantId: string;
  role: string;
  companyName: string;
  portalId: string;
}

interface UserInfo {
  name?: string | null;
  email?: string | null;
  role?: string | null;
}

interface TenantInfo {
  companyName?: string;
  portalId?: string;
}

interface Props {
  userInfo: UserInfo | null;
  tenantInfo: TenantInfo | null;
  onLogout: () => void;
}

function roleLabel(role?: string | null): string {
  return role === "owner" ? "オーナー" : role === "admin" ? "管理者" : role === "member" ? "メンバー" : "";
}

export default function AccountSwitcher({ userInfo, tenantInfo, onLogout }: Props) {
  const [open, setOpen] = useState(false);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  // クリック外で閉じる
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ドロップダウンを開いた時に memberships を取得
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.getMyMemberships()
      .then((r) => {
        setMemberships(r.memberships || []);
        setCurrentUserId(r.currentUserId);
      })
      .catch(() => { /* silent */ })
      .finally(() => setLoading(false));
  }, [open]);

  const handleSwitch = async (userId: string) => {
    setSwitching(userId);
    try {
      await api.switchAccount(userId);
      // Cookie が更新されたのでページ全体リロード（テナント固有のデータをすべて取り直す）
      window.location.href = "/";
    } catch (e: any) {
      alert(`アカウント切替に失敗しました: ${e?.message || ""}`);
      setSwitching(null);
    }
  };

  const otherMemberships = memberships.filter((m) => m.userId !== currentUserId);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: "4px 8px",
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div style={{ width: 34, height: 34, borderRadius: "50%", background: "#E6F1FB", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600, color: "#185FA5" }}>
          {(userInfo?.name || userInfo?.email || "?").slice(0, 2).toUpperCase()}
        </div>
        <div style={{ textAlign: "left" }}>
          <div style={{ fontSize: 14, fontWeight: 500 }}>{userInfo?.name || userInfo?.email || "—"}</div>
          <div style={{ fontSize: 12, color: "#6b6b68" }}>
            {roleLabel(userInfo?.role)}
          </div>
        </div>
        <span style={{ fontSize: 10, color: "#999", marginLeft: 2 }}>▼</span>
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            marginTop: 6,
            minWidth: 280,
            background: "white",
            border: "1px solid #e8e7e3",
            borderRadius: 10,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            zIndex: 1000,
            overflow: "hidden",
          }}
        >
          {/* 現ユーザー / テナント */}
          <div style={{ padding: "14px 16px", borderBottom: "1px solid #f0efeb" }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{userInfo?.name || userInfo?.email || "—"}</div>
            <div style={{ fontSize: 11, color: "#6b6b68" }}>{userInfo?.email}</div>
          </div>

          {/* 現テナント表示 */}
          <div style={{ padding: "10px 16px", background: "#F1EEFC" }}>
            <div style={{ fontSize: 10, color: "#534AB7", fontWeight: 600, marginBottom: 2 }}>現在のテナント</div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{tenantInfo?.companyName || "—"}</div>
            <div style={{ fontSize: 10, color: "#6b6b68" }}>Portal ID: {tenantInfo?.portalId}</div>
          </div>

          {/* 他の所属テナント */}
          {loading && (
            <div style={{ padding: "14px 16px", fontSize: 12, color: "#999", textAlign: "center" }}>
              読み込み中...
            </div>
          )}
          {!loading && otherMemberships.length > 0 && (
            <div style={{ borderTop: "1px solid #f0efeb" }}>
              <div style={{ padding: "8px 16px 4px", fontSize: 10, color: "#6b6b68", fontWeight: 600, textTransform: "uppercase" }}>
                アカウントを切替
              </div>
              {otherMemberships.map((m) => (
                <button
                  key={m.userId}
                  onClick={() => handleSwitch(m.userId)}
                  disabled={switching === m.userId}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    padding: "10px 16px",
                    background: "none",
                    border: "none",
                    cursor: switching === m.userId ? "wait" : "pointer",
                    borderTop: "1px solid #f8f7f5",
                    opacity: switching === m.userId ? 0.5 : 1,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#faf9f7")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
                >
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{m.companyName}</div>
                  <div style={{ fontSize: 11, color: "#6b6b68" }}>
                    Portal ID: {m.portalId} · {roleLabel(m.role)}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* ログアウト */}
          <div style={{ borderTop: "1px solid #f0efeb" }}>
            <button
              onClick={onLogout}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "12px 16px",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: 13,
                color: "#6b6b68",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#faf9f7")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
            >
              ログアウト
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
